import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Image from "@/atoms/Image";
import H2 from "@/components/atoms/text/H2";
import H3 from "@/components/atoms/text/H3";
import Para from "@/components/atoms/text/Para";
import { RoundedTickIcon, PointArrowIcon } from "@/components/atoms/Icons";

const useScrollImageChange = (toolsData, stickyOffset = 200) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const contentRefs = useRef([]);

  const handleScroll = useCallback(() => {
    contentRefs.current.forEach((contentEl, index) => {
      if (contentEl) {
        const rect = contentEl.getBoundingClientRect();
        if (rect.top >= stickyOffset && rect.top <= stickyOffset + 100) {
          setCurrentImgIndex(index);
        }
      }
    });
  }, [stickyOffset]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return { currentImgIndex, contentRefs };
};

const ToolItem = ({ tool, index, contentRef }) => (
  <li
    className={`content-li mt-10-d ${
      index !== tool.length - 1 ? "pb-15-d" : ""
    }`}
    data-index={index}
    ref={contentRef}
  >
    <div className="fx fx--sb">
      <H3 desktopSize="sm" className="bold mb-4-d">
        {tool.title}
        <span className="dot">.</span>
      </H3>
      <a className="arrow-btn" href={tool.link}>
        <PointArrowIcon
          className="arrow-icon"
          color="#33393D"
          width="20"
          height="20"
        />
      </a>
    </div>
    <ul className="mb-7">
      {tool.items.map((item, idx) => (
        <li key={idx} className="mb-3 fx fx-v">
          <RoundedTickIcon className="mr-2" />
          <Para desktopSize="lg" mobileSize="lg">
            {item}
          </Para>
        </li>
      ))}
    </ul>
  </li>
);

const ToolsSection = ({ toolsData, title, description }) => {
  const stickyOffset = 200;
  const { currentImgIndex, contentRefs } = useScrollImageChange(
    toolsData,
    stickyOffset
  );
  const memoizedToolsData = useMemo(() => toolsData, [toolsData]);

  return (
    <section className="tools-section bc-blue-light pv-15-d pv-8">
      <div className="container">
        <H2
          className="bold mb-3-d mb-5 ta-center"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <Para
          desktopSize="lg"
          mobileSize="lg"
          className="ta-center mb-10-d mb-8"
        >
          {description}
        </Para>

        <div className="fx-d fx--sb scroll-animate-wrapper">
          <ul className="img-wrapper">
            {memoizedToolsData.map((tool, index) => (
              <li
                key={index}
                className={`img-li ${
                  index === currentImgIndex ? "active" : ""
                }`}
              >
                <Image
                  fileName={tool.image}
                  fileFormat="png"
                  desktopWidth="540"
                  mobileWidth="335"
                  alt={tool.title}
                />
              </li>
            ))}
          </ul>

          <ul className="content-wrapper">
            {memoizedToolsData.map((tool, index) => (
              <ToolItem
                key={index}
                tool={tool}
                index={index}
                contentRef={(el) => (contentRefs.current[index] = el)}
              />
            ))}
          </ul>
        </div>
      </div>

      <style jsx>{`
        .scroll-animate-wrapper {
          display: flex;
          position: relative;
        }

        .img-wrapper,
        .content-wrapper {
          width: 50%;
          max-width: 540px;
        }

        .img-wrapper {
          position: sticky;
          top: ${stickyOffset}px;
          height: min(50vh, 400px);
          min-height: 400px;
        }

        .img-li {
          position: absolute;
          width: 100%;
          transition: opacity 0.5s ease-in-out;
          opacity: 0;
          visibility: hidden;
          height: 0;
        }

        .img-li.active {
          opacity: 1;
          visibility: visible;
          height: auto;
        }
      `}</style>
    </section>
  );
};

export default ToolsSection;
